// src/components/ConfirmarEmailBanner.js — faixa "Confirme seu e-mail" (out/26).
//
// PR 2 da recuperação de senha: o cadastro manda um código de 6 dígitos (vale
// 24 h) e esta faixa, no topo de toda tela com TopBar, pede a confirmação —
// SEM bloquear nada. Aparece só quando GET /users/me diz
// `email_confirmacao_pendente` (cadastro novo, ainda não confirmado, com o
// envio de e-mail ligado no servidor): contas antigas e atendentes nunca veem.
//
// Enquanto não confirmou, dá pra CORRIGIR o e-mail (digitado errado no
// cadastro, o código nunca chegaria). O e-mail é também o login, então a
// faixa diz isso e atualiza o `user` do localStorage.
import { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { FiMail } from 'react-icons/fi';
import api from '../services/Api';

const COR = '#5BA4F5';
const BORDA = 'rgba(91,164,245,0.40)';
const FUNDO = 'rgba(91,164,245,0.08)';
const VERDE = '#00D67C';
const VERMELHO = '#FF6B81';

const campo = {
  bgcolor: '#131C30', color: '#DDE4F0', border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: '6px', px: 1, py: 0.45, fontFamily: 'inherit', fontSize: '0.78rem', outline: 'none',
  '&:focus': { borderColor: COR },
};
const link = {
  background: 'none', border: 'none', p: 0, cursor: 'pointer', fontFamily: 'inherit',
  fontSize: '0.7rem', color: '#7A8799', textDecoration: 'underline',
  '&:hover:not(:disabled)': { color: COR }, '&:disabled': { cursor: 'default', textDecoration: 'none', opacity: 0.7 },
  '&:focus-visible': { outline: `2px solid ${COR}`, outlineOffset: 2 },
};
const botao = {
  bgcolor: COR, color: '#0A0F1A', borderRadius: '6px', fontFamily: 'inherit',
  fontSize: '0.7rem', fontWeight: 700, textTransform: 'none', px: 1.3, py: 0.4, minWidth: 0,
  '&:hover': { bgcolor: '#7CB6F7' }, '&.Mui-disabled': { bgcolor: 'rgba(91,164,245,0.35)', color: '#0A0F1A' },
};

function erroDe(err, padrao) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (!err?.response) return 'Não foi possível conectar. Verifique sua conexão.';
  return padrao;
}

export default function ConfirmarEmailBanner() {
  const [me, setMe] = useState(null);
  const [modo, setModo] = useState('codigo'); // 'codigo' | 'corrigir' | 'feito'
  const [codigo, setCodigo] = useState('');
  const [novoEmail, setNovoEmail] = useState('');
  const [msg, setMsg] = useState({ tipo: '', texto: '' });
  const [ocupado, setOcupado] = useState(false);
  const [reenviarEm, setReenviarEm] = useState(0);

  useEffect(() => {
    if (!localStorage.getItem('token')) return undefined;
    let vivo = true;
    api.get('/users/me').then((r) => { if (vivo) setMe(r.data); }).catch(() => {});
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    if (reenviarEm <= 0) return undefined;
    const t = setTimeout(() => setReenviarEm((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [reenviarEm]);

  useEffect(() => {
    if (modo !== 'feito') return undefined;
    const t = setTimeout(() => setMe(null), 6000);
    return () => clearTimeout(t);
  }, [modo]);

  if (!me || (!me.email_confirmacao_pendente && modo !== 'feito')) return null;

  const so6 = codigo.replace(/\D/g, '');

  const confirmar = async (e) => {
    e.preventDefault();
    if (so6.length !== 6 || ocupado) return;
    setOcupado(true);
    setMsg({ tipo: '', texto: '' });
    try {
      await api.post('/auth/email/confirmar', { codigo: so6 });
      setModo('feito');
    } catch (err) {
      setMsg({ tipo: 'erro', texto: erroDe(err, 'Não foi possível confirmar. Tente de novo.') });
    } finally {
      setOcupado(false);
    }
  };

  const reenviar = async () => {
    setOcupado(true);
    setMsg({ tipo: '', texto: '' });
    try {
      const { data } = await api.post('/auth/email/enviar-codigo');
      setMsg({ tipo: 'ok', texto: data.mensagem });
      setReenviarEm(data.reenviar_em || 60);
      setCodigo('');
    } catch (err) {
      // O servidor manda a espera no corpo (o CORS esconde o Retry-After).
      const espera = parseInt(err?.response?.data?.espera, 10);
      if (espera > 0) setReenviarEm(espera);
      setMsg({ tipo: 'erro', texto: erroDe(err, 'Não foi possível reenviar. Tente de novo.') });
    } finally {
      setOcupado(false);
    }
  };

  const corrigir = async (e) => {
    e.preventDefault();
    if (!novoEmail.trim() || ocupado) return;
    setOcupado(true);
    setMsg({ tipo: '', texto: '' });
    try {
      const { data } = await api.post('/auth/email/corrigir', { email: novoEmail.trim() });
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...u, email: data.email }));
      } catch { /* só o cache do usuário */ }
      setMe((m) => ({ ...m, email: data.email }));
      setModo('codigo');
      setCodigo('');
      setNovoEmail('');
      setReenviarEm(data.reenviar_em || 0);
      setMsg({ tipo: 'ok', texto: data.mensagem });
    } catch (err) {
      setMsg({ tipo: 'erro', texto: erroDe(err, 'Não foi possível trocar o e-mail. Tente de novo.') });
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Box
      role="region"
      aria-label="Confirmação do e-mail"
      sx={{
        bgcolor: '#0E1525', borderBottom: `1px solid ${modo === 'feito' ? 'rgba(0,214,124,0.35)' : BORDA}`,
        fontFamily: "'Syne', sans-serif",
      }}
    >
      <Box sx={{
        px: { xs: 1.2, md: 3 }, py: 0.9, bgcolor: modo === 'feito' ? 'rgba(0,214,124,0.08)' : FUNDO,
        display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap',
      }}>
        <FiMail size={14} color={modo === 'feito' ? VERDE : COR} aria-hidden="true" />

        {modo === 'feito' && (
          <Typography role="status" sx={{ fontSize: '0.75rem', color: '#fff', fontFamily: 'inherit' }}>
            <b style={{ color: VERDE }}>E-mail confirmado.</b> É por ele que você recupera a senha, se precisar.
          </Typography>
        )}

        {modo === 'codigo' && (
          <>
            <Typography sx={{ fontSize: '0.75rem', color: '#fff', fontFamily: 'inherit', flex: '1 1 260px' }}>
              <b style={{ color: COR }}>Confirme seu e-mail.</b> Enviamos um código de 6 dígitos para{' '}
              <b>{me.email}</b>.
            </Typography>
            <Box component="form" onSubmit={confirmar} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Box
                component="input" value={codigo} aria-label="Código de 6 dígitos"
                onChange={(e) => setCodigo(e.target.value.replace(/[^\d ]/g, '').slice(0, 7))}
                inputMode="numeric" autoComplete="one-time-code" placeholder="000000"
                sx={{ ...campo, width: 92, letterSpacing: '0.25em', fontVariantNumeric: 'tabular-nums' }}
              />
              <Button type="submit" disabled={so6.length !== 6 || ocupado} sx={botao}>
                {ocupado ? 'Confirmando…' : 'Confirmar'}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box component="button" type="button" onClick={reenviar} disabled={reenviarEm > 0 || ocupado} sx={link}>
                {reenviarEm > 0 ? `Reenviar em ${reenviarEm} s` : 'Reenviar código'}
              </Box>
              <Box component="button" type="button" sx={link}
                onClick={() => { setModo('corrigir'); setMsg({ tipo: '', texto: '' }); setNovoEmail(''); }}>
                E-mail errado? Corrigir
              </Box>
            </Box>
          </>
        )}

        {modo === 'corrigir' && (
          <Box component="form" onSubmit={corrigir}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap', flex: '1 1 auto' }}>
            <Typography component="label" htmlFor="corrigir-email"
              sx={{ fontSize: '0.75rem', color: '#fff', fontFamily: 'inherit' }}>
              Qual é o e-mail certo? Ele passa a ser o seu login.
            </Typography>
            <Box
              component="input" id="corrigir-email" type="email" value={novoEmail} autoComplete="email"
              onChange={(e) => setNovoEmail(e.target.value)} placeholder="seu@empresa.com.br"
              sx={{ ...campo, width: { xs: '100%', sm: 240 } }}
            />
            <Button type="submit" disabled={!novoEmail.trim() || ocupado} sx={botao}>
              {ocupado ? 'Salvando…' : 'Salvar e enviar código'}
            </Button>
            <Box component="button" type="button" sx={link} disabled={ocupado}
              onClick={() => { setModo('codigo'); setMsg({ tipo: '', texto: '' }); }}>
              Cancelar
            </Box>
          </Box>
        )}

        <Box aria-live="polite" sx={{ flexBasis: '100%', display: msg.texto ? 'block' : 'none' }}>
          <Typography sx={{ fontSize: '0.7rem', fontFamily: 'inherit', color: msg.tipo === 'erro' ? VERMELHO : '#DDE4F0' }}>
            {msg.texto}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
